"use client";

import React, { useState, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';

interface Testimonial {
  content: string;
  author: string;
  role: string;
  company: string;
}

const testimonials: Testimonial[] = [
  {
    content: "Switching to the Premium Support package was the best business decision we made this year. Our customers are happier, and we've seen a 22% increase in repeat purchases.",
    author: "Amanda Lee",
    role: "Founder",
    company: "Eco Essentials"
  },
  {
    content: "Working with them on our website was great. The online ordering system has really helped us stay organized during rush hours, and they made sure everything was easy for our staff to use. Really appreciate how they took time to understand our needs.",
    author: "Maria Rodriguez",
    role: "Owner",
    company: "Casa Grande"
  },
  {
    content: "The digital transformation of our customer support has been remarkable. Our customer satisfaction scores have increased by 35% and we're seeing more repeat purchases.",
    author: "Jessica Miller",
    role: "Founder",
    company: "Bloom Beauty"
  },
  {
    content: "The Leadership Lab workshop completely changed how our team approaches customer service. We now have systems in place that have reduced response times by 60% and improved team morale.",
    author: "Robert Thompson",
    role: "CEO",
    company: "Urban Gear Co."
  }
];

export function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => goToNext(),
    onSwipedRight: () => goToPrevious(),
    trackMouse: true
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const getVisibleTestimonials = () => {
    const visibleTestimonials = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % testimonials.length;
      visibleTestimonials.push(testimonials[index]);
    }
    return visibleTestimonials;
  };

  return (
    <section className="w-full bg-white">
      <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10 pb-16 md:pb-24">
        {/* Header */}
        <div className="flex flex-col items-center mb-16">
          <h2 className="text-2xl md:text-4xl font-bold uppercase text-center mb-4 leading-tight" style={{ color: 'rgb(29,29,31)', fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif', letterSpacing: '0.025em' }}>
            Don&apos;t just take our word for it
          </h2>
          <p className="text-base md:text-lg text-center max-w-2xl" style={{ color: 'rgba(0,0,0,0.88)' }}>
            Hear directly from our clients about their experiences working with us
          </p>
        </div>
        
        <div className="relative" {...swipeHandlers}>
          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {getVisibleTestimonials().map((testimonial, index) => (
              <div
                key={index}
                className={`bg-white p-10 rounded-2xl border border-gray-200 transition-all duration-300 flex flex-col hover:border-gray-300 ${
                  index === 0 ? 'flex' : 'hidden md:flex'
                }`}
                style={{ minHeight: '320px' }}
              >
                {/* Content */}
                <div className="flex-grow mb-8">
                  <p className="text-[#171A20] text-base leading-relaxed italic">
                    &ldquo;{testimonial.content}&rdquo;
                  </p>
                </div>

                {/* Author Info */}
                <div className="mt-auto pt-6 border-t border-gray-200">
                  <p className="font-semibold text-[#171A20] mb-1">{testimonial.author}</p>
                  <p className="text-sm text-[#5C5E62]">
                    {testimonial.role}, {testimonial.company}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Buttons - Desktop */}
          <button
            onClick={goToPrevious}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 w-10 h-10 items-center justify-center text-gray-400 hover:text-[#171A20] transition-colors duration-300"
            aria-label="Previous testimonials"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={goToNext}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 w-10 h-10 items-center justify-center text-gray-400 hover:text-[#171A20] transition-colors duration-300"
            aria-label="Next testimonials"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Pagination Dots */}
          <div className="flex justify-center items-center mt-12 gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex 
                    ? 'w-8 h-2 bg-[#171A20]' 
                    : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to testimonial set ${index + 1}`}
              />
            ))}
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden justify-center gap-8 mt-8">
            <button
              onClick={goToPrevious}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-[#171A20] transition-colors duration-300"
              aria-label="Previous testimonials"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToNext}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-[#171A20] transition-colors duration-300"
              aria-label="Next testimonials"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}